import {
  HEBREW_MONTHS,
  HEBREW_MONTH_MAX_DAYS,
  hebrewDateOf,
  hebrewDayExists,
  hebrewMonthName,
  hebrewToCivil,
  isHebrewLeapYear,
  nextHebrewOccurrence,
  type CivilDate,
  type HebrewMonth,
} from './hebrew-calendar';

// The person card's CONTENT, as a pure model (plan P1.16, test C1 in native-apps/shared/people-import/TEST_PLAN.md).
//
// `personCardModel(entry, now)` turns one contact-book entry (a BFF `ContactEntry` row) into the rows the
// card renders: relationships, important dates (with the next occurrence, the age it brings and the days
// until it), social profiles, the organization, and the facts with their no-suggest badge — each with the
// actions the card offers on it. `ContactDetailBody` draws exactly this; iOS (`PersonCardModel`) and Android
// (`PersonCardModel`) build the SAME model from the SAME entry, and all three are pinned to
// `native-apps/shared/people-import/person-card.fixture.json`.
//
// Kept free of React and of i18n keys on purpose: web jest has no DOM, and the model must never carry a
// token the card is forbidden to show — `DerivedSignals` (family/work/minor/closeness) drives behaviour and
// is NEVER rendered (§1.1), and a provenance source is never shown as its raw enum. The UI maps each row's
// tokens to copy (`PERSON_CARD_COPY_KEYS`).

/**
 * The caps the card enforces — the SAME numbers person.proto declares as its own list caps (`relationships` max 3,
 * `important_dates` max 8, `social_profiles` max 4) and the server's `too_many_values` answers with as `max`.
 */
export const PERSON_CARD_CAPS = { relationships: 3, importantDates: 8, socialProfiles: 4 } as const;

// ---- the wire (ContactEntry's additive keys, P1.2) ----------------------------------------------------

export interface WireProvenance {
  source?: string;
  confidence?: string;
  sourceUrl?: string;
  setAt?: string;
}

export interface WireRelationship {
  relation?: string;
  relatedToPersonId?: string;
  provenance?: WireProvenance;
  /** Server-derived, readOnly. Never rendered, never offered as a choice. */
  derived?: { family?: boolean; work?: boolean; minor?: boolean; closeness?: string };
}

export interface WireImportantDate {
  kind?: string;
  calendar?: string;
  month?: number;
  day?: number;
  year?: number;
  hebrewMonth?: string;
  label?: string;
  provenance?: WireProvenance;
}

export interface WireSocialProfile {
  network?: string;
  handle?: string;
  url?: string;
  provenance?: WireProvenance;
}

export interface WireOrganization {
  company?: string;
  title?: string;
  website?: string;
  provenance?: WireProvenance;
}

export interface WireFact {
  text?: string;
  kind?: string;
  sensitive?: boolean;
  noSuggest?: boolean;
}

/** The parts of a contact-book entry the card model reads. */
export interface PersonCardEntry {
  /** The entry's person — the card is person-backed, so an entry without a person id has no card. */
  contact?: { personId?: string };
  facts?: string[];
  relationships?: WireRelationship[];
  importantDates?: WireImportantDate[];
  socialProfiles?: WireSocialProfile[];
  organization?: WireOrganization | null;
  factEntries?: WireFact[];
  /** BFF `ContactEntry.relationship_summary` (field 19, P3.1) — derived by People, read-only, enums by name. */
  relationshipSummary?: WireRelationshipSummary | null;
  /** BFF `ContactWithOutreaches.relationship_profile` (field 20, P2.3) — person-detail reads only; enums by name. */
  relationshipProfile?: WireRelationshipProfile | null;
}

/** The stated half: what the USER said about how they relate to the person (the card edits it). */
export interface WireStatedRelationshipProfile {
  feeling?: string;
  feelingWords?: string;
  closeness?: string;
  register?: string;
  toneNote?: string;
  address?: string;
  avoid?: string[];
  doNotApproach?: boolean;
  provenance?: WireProvenance;
}

/** The learned half: what Symphony inferred — read-only on every surface. */
export interface WireLearnedRelationshipProfile {
  style?: { register?: string; language?: string; greeting?: string; signOff?: string; length?: string; note?: string };
  topics?: string[];
  summary?: string;
  feeling?: string;
  confidence?: string;
  evidence?: { kind?: string; reason?: string; occurredAt?: string | Date; sourceRef?: string }[];
  computedAt?: string | Date;
}

export interface WireRelationshipProfile {
  stated?: WireStatedRelationshipProfile;
  learned?: WireLearnedRelationshipProfile;
}

export interface WireRelationshipSummary {
  trend?: string;
  initiation?: string;
  typicalChannel?: string;
  /** Empty in v1 (People computes only the rule-based parts); never read by the card yet. */
  recurringTopics?: string[];
  summary?: string;
  computedAt?: string | Date;
}

/** The shape `withPersonCardDefaults` needs — loose, so a generated BFF row and a `WebContact` both fit. */
export interface PersonDetailEntry {
  contact?: { personId?: string };
  relationships?: unknown[];
  importantDates?: unknown[];
  socialProfiles?: unknown[];
  organization?: unknown;
  relationshipProfile?: unknown;
}

/**
 * A PERSON-DETAIL read of an entry (one person, by `personId`) as the card must hold it. Proto3 JSON OMITS an empty
 * list, so "this person has no relationships" and "this server predates relationships" arrive identically; every
 * person-detail read path therefore REPLACES the structured fields with what this read carried — the three lists
 * defaulting to `[]`, the organization to `null` — and never merges them over a cached copy (where an omitted empty
 * list would leave the stale values standing). The relationship profile (P2.3) is replaced the same way, defaulting
 * to `null`: absent after a person-detail read means none.
 *
 * The defaulted lists are also the card's "this entry can hold the fields" marker (`structured` below): only a
 * client that knows the fields stamps them, on a read of one person, and the BFF has served them since VMR #31832.
 * Applied by `/api/contact-book` (personId), `/api/contact-person` (the edit's re-read and its partial answer) and
 * `editPersonCard`; iOS `PersonCardEntry.personDetail` and Android `PersonCardWire.personDetail` are the twins.
 * An entry with no person id is returned untouched — it has no card.
 */
export function withPersonCardDefaults<T extends PersonDetailEntry>(entry: T): T {
  if (!text(entry.contact?.personId)) return entry;
  return {
    ...entry,
    relationships: entry.relationships ?? [],
    importantDates: entry.importantDates ?? [],
    socialProfiles: entry.socialProfiles ?? [],
    organization: entry.organization ?? null,
    relationshipProfile: entry.relationshipProfile ?? null,
  } as T;
}

/**
 * The cached entry after a card edit's answer. A whole answer IS the person's fresh entry and REPLACES the cached
 * one — never merged over it, because proto3 omits an empty list and a merge would keep the value the edit just
 * forgot. A `partial` answer (the edit landed, the re-read failed) replaces only the structured fields — the lists
 * defaulting to [] and the organization to null — and the caller re-reads. `editPersonCard` (`lib/contact-book.ts`).
 * The relationship profile rides a partial answer only to a relationship-profile edit, and only when the server could
 * read it back: present (`{}` = none left) it REPLACES the cached one; absent, the cached one stays until the re-read.
 */
export function applyPersonCardAnswer<T extends PersonDetailEntry>(old: T | undefined, answer: T & { partial?: boolean }): T | undefined {
  const { partial, ...entry } = answer;
  if (!partial) return withPersonCardDefaults(entry as T);
  if (!old) return old;
  const fresh = withPersonCardDefaults({ ...entry, contact: { ...old.contact, ...entry.contact } } as T);
  return {
    ...old,
    relationships: fresh.relationships,
    importantDates: fresh.importantDates,
    socialProfiles: fresh.socialProfiles,
    organization: fresh.organization,
    ...('relationshipProfile' in entry ? { relationshipProfile: fresh.relationshipProfile } : {}),
  };
}

// ---- the model -------------------------------------------------------------------------------------------

export type CardAction = 'edit' | 'forget';
export type CardDateKind = 'birthday' | 'anniversary' | 'memorial' | 'nameDay' | 'custom';
export type CardNetwork = 'instagram' | 'x' | 'linkedin' | 'facebook' | 'tiktok' | 'youtube' | 'github' | 'website' | 'other';

export interface CardRelationshipRow {
  text: string;
  /** The anchor of a second-degree relation ("Yoni's teacher") — a person id, never rendered; null for most. */
  relatedToPersonId: string | null;
  /** "Suggested by Symphony" — the value is INFERRED (or UNCERTAIN), not something the user said. */
  inferred: boolean;
  actions: CardAction[];
}

export interface CardDateRow {
  kind: CardDateKind;
  calendar: 'gregorian' | 'hebrew';
  day: number;
  /** Gregorian month 1-12; null for a Hebrew date. */
  month: number | null;
  /** The stored Hebrew month; null for a Gregorian date. */
  hebrewMonth: HebrewMonth | null;
  /** In the date's own calendar (5771 for a Hebrew date); null when unknown, which is normal. */
  year: number | null;
  label: string | null;
  /** The age a BIRTHDAY brings on its next occurrence; null without a year and for every other kind. */
  age: number | null;
  /** Whole days from today to the next occurrence (0 = today); null when it cannot fall again. */
  daysUntil: number | null;
  /** The next occurrence as YYYY-MM-DD (civil), or null. */
  next: string | null;
  inferred: boolean;
  actions: CardAction[];
}

export interface CardProfileRow {
  network: CardNetwork;
  handle: string | null;
  /** The URL the row opens — the stored one, else built from the network + handle; null when neither. */
  opens: string | null;
  inferred: boolean;
  actions: CardAction[];
}

export interface CardOrganization {
  company: string | null;
  title: string | null;
  website: string | null;
  inferred: boolean;
  actions: CardAction[];
}

export interface CardFactRow {
  text: string;
  /** The fact is `no_suggest` (health, relationship trouble, religion): never used in suggestions. */
  noSuggestBadge: boolean;
}

export type CardSummaryTrend = 'warming' | 'steady' | 'cooling' | 'dormant';
export type CardSummaryInitiation = 'userMostly' | 'balanced' | 'themMostly';
export type CardSummaryChannel = 'whatsapp' | 'email' | 'sms' | 'phoneCall' | 'meetings' | 'telegram' | 'instagram';

/**
 * "How you two keep in touch" (P3.1): what People OBSERVED — who usually reaches out, where, and whether it is
 * getting quieter. Each part is null when People could not say (UNKNOWN, too few messages) or named something the
 * card has no words for; a summary with no part left is no summary (`PersonCardModel.summary` null).
 */
export interface CardSummary {
  initiation: CardSummaryInitiation | null;
  channel: CardSummaryChannel | null;
  trend: CardSummaryTrend | null;
}

export type CardFeeling = 'close' | 'warm' | 'neutral' | 'distant';
export type CardCloseness = 'innerCircle' | 'close' | 'regular' | 'peripheral';
export type CardRegister = 'intimate' | 'casual' | 'friendlyProfessional' | 'formal';
export type CardStyleLength = 'short' | 'medium' | 'long';

/**
 * "How you relate" (P2.3) — the STATED half the user set (each part null/empty when not set, which the card draws
 * as its "Add"), plus Symphony's LEARNED read behind the collapsed "How I read this". Every enum arrives as the
 * card's token, never the raw name.
 */
export interface CardRelate {
  feeling: { value: CardFeeling; words: string | null } | null;
  /** The user's closeness override; null = none, so the relation's derived closeness applies ("From the relationship"). */
  closeness: CardCloseness | null;
  tone: { register: CardRegister | null; note: string | null } | null;
  /** What to call them. */
  address: string | null;
  /** Never bring up — each one removable. */
  avoid: string[];
  canAddAvoid: boolean;
  doNotApproach: boolean;
  /** Symphony's read, or null when there is nothing to show (no disclosure is drawn). */
  learned: CardLearned | null;
}

export interface CardLearned {
  /** One line: register · language · greeting / sign-off · length, then the note. Null when none of it is known. */
  style: {
    register: CardRegister | null;
    /** A BCP-47 language code ("he"), named in the reader's language by the UI. */
    language: string | null;
    greeting: string | null;
    signOff: string | null;
    length: CardStyleLength | null;
    note: string | null;
  } | null;
  topics: string[];
  summary: string | null;
  /**
   * "Seems warm" — null when the user stated a feeling (theirs hides Symphony's), when it came with no confidence, or
   * when its newest evidence is more than 90 days old (`LEARNED_FEELING_MAX_AGE_MS`).
   */
  feeling: CardFeeling | null;
  /**
   * The reasons behind the read. An `ownerWords` line is drawn ONLY as "From what you said on {date}": its reason —
   * the user's own words, paraphrased — is never carried into the model, so no surface can print it.
   */
  evidence: { kind: 'ownerWords' | 'other'; reason: string | null; occurredAt: string | null }[];
}

export interface PersonCardModel {
  /**
   * The card draws its sections — every one, empty included, so the first value can be added. True for a
   * PERSON-backed entry (a `contact.personId`) that carries the structured keys, which every person-detail read
   * stamps (`withPersonCardDefaults`) even when the person has no values yet. False for an entry with no person,
   * and for one with none of the keys — a list row, or a server that predates them (`legacyEntry` in the
   * fixture): the card then shows NO new section, not an empty one.
   */
  structured: boolean;
  relationships: CardRelationshipRow[];
  importantDates: CardDateRow[];
  socialProfiles: CardProfileRow[];
  organization: CardOrganization | null;
  facts: CardFactRow[];
  /** The relationship summary, or null when there is none or none of it is known (no row is drawn). */
  summary: CardSummary | null;
  /** "How you relate" (P2.3) — always present; drawn whenever the sections are (`structured`). */
  relate: CardRelate;
  /** Whether another value may be added to each section — false at the cap. */
  canAdd: { relationships: boolean; importantDates: boolean; socialProfiles: boolean };
}

const DATE_KINDS: Record<string, CardDateKind> = {
  BIRTHDAY: 'birthday',
  ANNIVERSARY: 'anniversary',
  MEMORIAL: 'memorial',
  NAME_DAY: 'nameDay',
  CUSTOM: 'custom',
};

const NETWORKS: Record<string, CardNetwork> = {
  INSTAGRAM: 'instagram',
  X: 'x',
  LINKEDIN: 'linkedin',
  FACEBOOK: 'facebook',
  TIKTOK: 'tiktok',
  YOUTUBE: 'youtube',
  GITHUB: 'github',
  WEBSITE: 'website',
  OTHER: 'other',
};

/** Where a bare handle opens, per network — only for networks whose profile URL is the handle. */
const PROFILE_URL: Partial<Record<CardNetwork, (handle: string) => string>> = {
  instagram: (h) => `https://www.instagram.com/${h}`,
  x: (h) => `https://x.com/${h}`,
  linkedin: (h) => `https://www.linkedin.com/in/${h}`,
  facebook: (h) => `https://www.facebook.com/${h}`,
  tiktok: (h) => `https://www.tiktok.com/@${h}`,
  youtube: (h) => `https://www.youtube.com/@${h}`,
  github: (h) => `https://github.com/${h}`,
};

const SUMMARY_TRENDS: Record<string, CardSummaryTrend> = { WARMING: 'warming', STEADY: 'steady', COOLING: 'cooling', DORMANT: 'dormant' };
const SUMMARY_INITIATIONS: Record<string, CardSummaryInitiation> = { USER_MOSTLY: 'userMostly', BALANCED: 'balanced', THEM_MOSTLY: 'themMostly' };
/**
 * CommunicationChannel names as the card says them. Connector transports fold onto the channel they land on (as
 * People's own tally does); APP, WEB, VOICE and EXTERNAL_AGENT are how the user talks to the AGENT, not to a person.
 */
const SUMMARY_CHANNELS: Record<string, CardSummaryChannel> = {
  WHATSAPP: 'whatsapp',
  WHATSAPP_CONNECTOR: 'whatsapp',
  EMAIL: 'email',
  GMAIL_CONNECTOR: 'email',
  SMS: 'sms',
  PHONE_CALL: 'phoneCall',
  CALENDAR_MEETINGS: 'meetings',
  TELEGRAM: 'telegram',
  INSTAGRAM_CONNECTOR: 'instagram',
};

const enumName = (value: string | undefined | null) => (value ?? '').trim().toUpperCase();

const FEELINGS: Record<string, CardFeeling> = { CLOSE: 'close', WARM: 'warm', NEUTRAL: 'neutral', DISTANT: 'distant' };
const CLOSENESSES: Record<string, CardCloseness> = { INNER_CIRCLE: 'innerCircle', CLOSE: 'close', REGULAR: 'regular', PERIPHERAL: 'peripheral' };
const REGISTERS: Record<string, CardRegister> = {
  INTIMATE: 'intimate',
  CASUAL: 'casual',
  FRIENDLY_PROFESSIONAL: 'friendlyProfessional',
  FORMAL: 'formal',
};
const LENGTHS: Record<string, CardStyleLength> = { SHORT: 'short', MEDIUM: 'medium', LONG: 'long' };
/** How many topics the user may list under "Never bring up" — person.proto's cap. */
export const RELATE_AVOID_MAX = 5;

const isoOf = (value: string | Date | undefined): string | null => {
  if (!value) return null;
  const at = value instanceof Date ? value : new Date(value);
  return Number.isNaN(at.getTime()) ? null : at.toISOString();
};

/**
 * A learned reading older than this, by its newest evidence, is not shown: it decays on the read side. The same rule
 * as the harnesses (VMR `RelationshipProfileUse.usableLearnedFeeling`, TS `usableLearnedFeeling`), iOS
 * `PersonCardRelate` and Android `CardRelate`.
 */
export const LEARNED_FEELING_MAX_AGE_MS = 90 * 24 * 60 * 60 * 1000;

/** The newest `evidence[].occurredAt`, else `computedAt`, else null (epoch ms). */
function newestEvidenceAt(learned: WireLearnedRelationshipProfile): number | null {
  let newest: number | null = null;
  for (const line of learned.evidence ?? []) {
    const at = isoOf(line.occurredAt);
    if (at !== null && (newest === null || Date.parse(at) > newest)) newest = Date.parse(at);
  }
  if (newest !== null) return newest;
  const computed = isoOf(learned.computedAt);
  return computed === null ? null : Date.parse(computed);
}

/** "How you relate" as the card's tokens (see `CardRelate`). */
export function cardRelate(wire: WireRelationshipProfile | null | undefined, now: Date = new Date()): CardRelate {
  const stated = wire?.stated;
  const feeling = FEELINGS[enumName(stated?.feeling)];
  const register = REGISTERS[enumName(stated?.register)] ?? null;
  const toneNote = text(stated?.toneNote);
  const avoid = (stated?.avoid ?? []).flatMap((topic) => (text(topic) ? [text(topic) as string] : []));
  return {
    feeling: feeling ? { value: feeling, words: text(stated?.feelingWords) } : null,
    closeness: CLOSENESSES[enumName(stated?.closeness)] ?? null,
    tone: register || toneNote ? { register, note: toneNote } : null,
    address: text(stated?.address),
    avoid,
    canAddAvoid: avoid.length < RELATE_AVOID_MAX,
    doNotApproach: stated?.doNotApproach === true,
    learned: cardLearned(wire?.learned, !!feeling, now),
  };
}

function cardLearned(learned: WireLearnedRelationshipProfile | undefined, statedFeeling: boolean, now: Date): CardLearned | null {
  if (!learned) return null;
  const s = learned.style;
  const style = {
    register: REGISTERS[enumName(s?.register)] ?? null,
    language: text(s?.language),
    greeting: text(s?.greeting),
    signOff: text(s?.signOff),
    length: LENGTHS[enumName(s?.length)] ?? null,
    note: text(s?.note),
  };
  // The BFF already drops a feeling below MEDIUM; one that arrives with no confidence at all is never shown either,
  // and nor is one whose newest evidence is more than LEARNED_FEELING_MAX_AGE_MS old (a reading with no date stands).
  const newest = newestEvidenceAt(learned);
  const stale = newest !== null && newest < now.getTime() - LEARNED_FEELING_MAX_AGE_MS;
  const feeling = !statedFeeling && !stale && text(learned.confidence) ? FEELINGS[enumName(learned.feeling)] ?? null : null;
  const model: CardLearned = {
    style: Object.values(style).some((part) => part !== null) ? style : null,
    topics: (learned.topics ?? []).flatMap((topic) => (text(topic) ? [text(topic) as string] : [])),
    summary: text(learned.summary),
    feeling,
    evidence: (learned.evidence ?? []).flatMap((line) => {
      const ownerWords = enumName(line.kind) === 'OWNER_WORDS';
      const occurredAt = isoOf(line.occurredAt);
      const reason = ownerWords ? null : text(line.reason);
      // An owner-words line says only WHEN; without a date it says nothing.
      if (ownerWords ? !occurredAt : !reason) return [];
      return [{ kind: ownerWords ? ('ownerWords' as const) : ('other' as const), reason, occurredAt }];
    }),
  };
  return model.style || model.topics.length || model.summary || model.feeling || model.evidence.length ? model : null;
}

/** The relationship summary as the card's tokens; null when absent or when nothing in it is known. */
export function cardSummary(wire: WireRelationshipSummary | null | undefined): CardSummary | null {
  if (!wire) return null;
  const summary: CardSummary = {
    initiation: SUMMARY_INITIATIONS[enumName(wire.initiation)] ?? null,
    channel: SUMMARY_CHANNELS[enumName(wire.typicalChannel)] ?? null,
    trend: SUMMARY_TRENDS[enumName(wire.trend)] ?? null,
  };
  return summary.initiation || summary.channel || summary.trend ? summary : null;
}

const text = (value: string | undefined | null): string | null => {
  const trimmed = (value ?? '').trim();
  return trimmed ? trimmed : null;
};

/** INFERRED and UNCERTAIN are the agent's guesses; STATED (or unset) is what someone actually said. */
export function isInferred(provenance: WireProvenance | undefined): boolean {
  const confidence = (provenance?.confidence ?? '').toUpperCase();
  return confidence === 'INFERRED' || confidence === 'UNCERTAIN';
}

/** Only an http(s) URL is ever opened — a stored `javascript:` link must not become a live anchor. */
function safeUrl(value: string | undefined | null): string | null {
  const url = text(value);
  return url && /^https?:\/\//i.test(url) ? url : null;
}

const pad = (n: number) => String(n).padStart(2, '0');
const iso = (date: CivilDate) => `${date.year}-${pad(date.month)}-${pad(date.day)}`;
const utcOf = (date: CivilDate) => Date.UTC(date.year, date.month - 1, date.day);
const isLeap = (year: number) => (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
/** Leap-year month lengths: 29 February is a real birthday. */
export const GREGORIAN_MONTH_DAYS = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

/**
 * Whether a stored (month, day) is a day the calendar ever has. A malformed one (31 April, 30 Tevet) is still
 * DRAWN, with its stored values as they are, but it has no next occurrence, no countdown and no age — the
 * calendar would otherwise roll it into the next month.
 */
function dayExists(hebrewMonth: HebrewMonth | null, month: number | null, day: number): boolean {
  if (hebrewMonth) return day <= HEBREW_MONTH_MAX_DAYS[hebrewMonth];
  return month !== null && day <= GREGORIAN_MONTH_DAYS[month - 1];
}

/** The civil date `now` is in `timeZone` (default: the runtime's own). */
export function civilToday(now: Date, timeZone?: string): CivilDate {
  const parts = new Intl.DateTimeFormat('en-US', { year: 'numeric', month: 'numeric', day: 'numeric', timeZone }).formatToParts(now);
  const get = (type: string) => Number(parts.find((part) => part.type === type)?.value);
  return { year: get('year'), month: get('month'), day: get('day') };
}

/**
 * The next time a Gregorian (month, day) falls on or after `today`. 29 February falls on 28 February in a
 * common year — the day before the date that does not exist, as the Hebrew yahrzeit rule does.
 */
export function nextGregorianOccurrence(month: number, day: number, today: CivilDate): CivilDate {
  for (let year = today.year; ; year += 1) {
    const d = month === 2 && day === 29 && !isLeap(year) ? 28 : day;
    const candidate = { year, month, day: d };
    if (utcOf(candidate) >= utcOf(today)) return candidate;
  }
}

function dateRow(date: WireImportantDate, today: CivilDate): CardDateRow | null {
  const kind = DATE_KINDS[(date.kind ?? '').toUpperCase()] ?? 'custom';
  const hebrew = (date.calendar ?? '').toUpperCase() === 'HEBREW';
  const day = date.day ?? 0;
  const year = date.year && date.year > 0 ? date.year : null;
  const hebrewMonth = hebrew ? HEBREW_MONTHS.find((m) => m === (date.hebrewMonth ?? '').toUpperCase()) ?? null : null;
  const month = hebrew ? null : date.month && date.month >= 1 && date.month <= 12 ? date.month : null;
  // A date that cannot fall on any day is not drawn — the server refuses one at the write.
  if (day < 1 || day > 31 || (hebrew ? !hebrewMonth : !month)) return null;

  let next: CivilDate | null = null;
  let occurrenceYear: number | null = null;
  if (!dayExists(hebrewMonth, month, day)) {
    // Malformed (31 April, 30 Tevet): drawn as stored, with no occurrence.
  } else if (hebrew && hebrewMonth) {
    const found = nextHebrewOccurrence(hebrewMonth, day, year, kind === 'memorial', today);
    next = found?.date ?? null;
    occurrenceYear = found?.hebrewYear ?? null;
  } else if (month) {
    next = nextGregorianOccurrence(month, day, today);
    occurrenceYear = next.year;
  }
  const age = kind === 'birthday' && year !== null && occurrenceYear !== null && occurrenceYear >= year ? occurrenceYear - year : null;
  return {
    kind,
    calendar: hebrew ? 'hebrew' : 'gregorian',
    day,
    month,
    hebrewMonth,
    year,
    label: text(date.label),
    age,
    daysUntil: next ? Math.round((utcOf(next) - utcOf(today)) / 86_400_000) : null,
    next: next ? iso(next) : null,
    inferred: isInferred(date.provenance),
    actions: ['edit', 'forget'],
  };
}

function profileRow(profile: WireSocialProfile): CardProfileRow | null {
  const network = NETWORKS[(profile.network ?? '').toUpperCase()] ?? 'other';
  const handle = text(profile.handle)?.replace(/^@+/, '') || null;
  const builder = PROFILE_URL[network];
  const opens = safeUrl(profile.url) ?? (handle && builder ? builder(encodeURIComponent(handle)) : null);
  if (!handle && !opens) return null;
  // A profile is a pointer, not prose: it is removed and added again, never edited in place.
  return { network, handle, opens, inferred: isInferred(profile.provenance), actions: ['forget'] };
}

const STRUCTURED_KEYS: (keyof PersonCardEntry)[] = ['relationships', 'importantDates', 'socialProfiles', 'factEntries'];

export function personCardModel(entry: PersonCardEntry, now: Date, timeZone?: string): PersonCardModel {
  const today = civilToday(now, timeZone);
  const structured =
    !!text(entry.contact?.personId) && (STRUCTURED_KEYS.some((key) => entry[key] !== undefined && entry[key] !== null) || !!entry.organization);

  const relationships = (entry.relationships ?? []).flatMap((relationship): CardRelationshipRow[] => {
    const relation = text(relationship.relation);
    return relation
      ? [
          {
            text: relation,
            relatedToPersonId: text(relationship.relatedToPersonId),
            inferred: isInferred(relationship.provenance),
            actions: ['edit', 'forget'],
          },
        ]
      : [];
  });
  const importantDates = (entry.importantDates ?? []).flatMap((date) => {
    const row = dateRow(date, today);
    return row ? [row] : [];
  });
  const socialProfiles = (entry.socialProfiles ?? []).flatMap((profile) => {
    const row = profileRow(profile);
    return row ? [row] : [];
  });

  const org = entry.organization;
  const company = text(org?.company);
  const title = text(org?.title);
  const website = safeUrl(org?.website);
  const organization: CardOrganization | null =
    company || title || website ? { company, title, website, inferred: isInferred(org?.provenance), actions: ['edit', 'forget'] } : null;

  // `factEntries` carries the no-suggest mark; an entry without it (an older server) still lists `facts`.
  const facts: CardFactRow[] = entry.factEntries
    ? entry.factEntries.flatMap((fact) => {
        const value = text(fact.text);
        return value ? [{ text: value, noSuggestBadge: !!fact.noSuggest }] : [];
      })
    : (entry.facts ?? []).flatMap((fact) => {
        const value = text(fact);
        return value ? [{ text: value, noSuggestBadge: false }] : [];
      });

  return {
    structured,
    relationships,
    importantDates,
    socialProfiles,
    organization,
    facts,
    summary: cardSummary(entry.relationshipSummary),
    relate: cardRelate(entry.relationshipProfile, now),
    canAdd: {
      relationships: relationships.length < PERSON_CARD_CAPS.relationships,
      importantDates: importantDates.length < PERSON_CARD_CAPS.importantDates,
      socialProfiles: socialProfiles.length < PERSON_CARD_CAPS.socialProfiles,
    },
  };
}

// ---- copy ------------------------------------------------------------------------------------------------

/** The catalog key naming a date's kind. */
export function dateKindLabelKey(kind: CardDateKind): string {
  return `people.card.dates.${kind}`;
}

/** Brand names are proper nouns and stay as they are; only the two generic networks are copy. */
export const NETWORK_NAMES: Record<Exclude<CardNetwork, 'website' | 'other'>, string> = {
  instagram: 'Instagram',
  x: 'X',
  linkedin: 'LinkedIn',
  facebook: 'Facebook',
  tiktok: 'TikTok',
  youtube: 'YouTube',
  github: 'GitHub',
};

/**
 * The summary line's parts, in reading order (who reaches out · where · how it is going), as catalog keys — each a
 * whole phrase, so no language has to compose a preposition with a channel name. The UI joins them with " · ".
 */
export function cardSummaryKeys(summary: CardSummary): string[] {
  return [
    summary.initiation ? `people.card.summary.initiation.${summary.initiation}` : null,
    summary.channel ? `people.card.summary.channel.${summary.channel}` : null,
    summary.trend ? `people.card.summary.trend.${summary.trend}` : null,
  ].filter((key): key is string => key !== null);
}

/** The summary row's catalog keys — the title, the "from what Symphony sees" note, and every part. */
export const PERSON_CARD_SUMMARY_KEYS = [
  'people.card.summary',
  'people.card.summary.note',
  'people.card.summary.initiation.userMostly',
  'people.card.summary.initiation.balanced',
  'people.card.summary.initiation.themMostly',
  'people.card.summary.channel.whatsapp',
  'people.card.summary.channel.email',
  'people.card.summary.channel.sms',
  'people.card.summary.channel.phoneCall',
  'people.card.summary.channel.meetings',
  'people.card.summary.channel.telegram',
  'people.card.summary.channel.instagram',
  'people.card.summary.trend.warming',
  'people.card.summary.trend.steady',
  'people.card.summary.trend.cooling',
  'people.card.summary.trend.dormant',
] as const;

/** The catalog keys of "How you relate" (P2.3) — the same keys on all three surfaces. */
export const PERSON_CARD_RELATE_KEYS = [
  'people.card.relate.title',
  'people.card.relate.feeling',
  'people.card.relate.feeling.close',
  'people.card.relate.feeling.warm',
  'people.card.relate.feeling.neutral',
  'people.card.relate.feeling.distant',
  'people.card.relate.feelingWords.placeholder',
  'people.card.relate.closeness',
  'people.card.relate.closeness.innerCircle',
  'people.card.relate.closeness.close',
  'people.card.relate.closeness.regular',
  'people.card.relate.closeness.peripheral',
  'people.card.relate.closeness.fromRelationship',
  'people.card.relate.tone',
  'people.card.relate.tone.intimate',
  'people.card.relate.tone.casual',
  'people.card.relate.tone.friendlyProfessional',
  'people.card.relate.tone.formal',
  'people.card.relate.toneNote.placeholder',
  'people.card.relate.address',
  'people.card.relate.avoid',
  'people.card.relate.avoid.add',
  'people.card.relate.doNotApproach',
  'people.card.relate.doNotApproach.help',
  'people.card.relate.learned.toggle',
  'people.card.relate.learned.style',
  'people.card.relate.learned.topics',
  'people.card.relate.learned.summary',
  'people.card.relate.learned.feeling.close',
  'people.card.relate.learned.feeling.warm',
  'people.card.relate.learned.feeling.neutral',
  'people.card.relate.learned.feeling.distant',
  'people.card.relate.learned.evidence.ownerWords',
  'people.card.relate.learned.length.short',
  'people.card.relate.learned.length.medium',
  'people.card.relate.learned.length.long',
  'people.card.error.invalidRelationshipProfile',
] as const;

/** Every catalog key the card renders. The C1 test asserts each one exists in every locale. */
export const PERSON_CARD_COPY_KEYS = [
  'people.card.relationship',
  'people.card.relationship.placeholder',
  'people.card.relationship.add',
  'people.card.dates',
  'people.card.dates.birthday',
  'people.card.dates.anniversary',
  'people.card.dates.memorial',
  'people.card.dates.nameDay',
  'people.card.dates.custom',
  'people.card.dates.hebrew',
  'people.card.dates.kind',
  'people.card.dates.day',
  'people.card.dates.month',
  'people.card.dates.year',
  'people.card.dates.label',
  'people.card.dates.today',
  'people.card.dates.inDays.one',
  'people.card.dates.inDays.other',
  'people.card.dates.inOneDay',
  'people.card.dates.inTwoDays',
  'people.card.dates.turns',
  'people.card.social',
  'people.card.social.network',
  'people.card.social.handle',
  'people.card.social.website',
  'people.card.social.other',
  'people.card.work',
  'people.card.work.company',
  'people.card.work.title',
  'people.card.work.website',
  'people.card.source.inferred',
  'people.card.noSuggest',
  'people.card.forget',
  'people.card.forget.confirm',
  'people.card.error.invalidDate',
  'people.card.error.unknownPerson',
  'people.card.error.full',
  'people.card.error.failed',
  'people.card.error.invalidRelationship',
  'people.card.error.invalidSocialProfile',
  'people.card.error.invalidOrganization',
  'people.card.error.labelRequired',
  ...PERSON_CARD_SUMMARY_KEYS,
  ...PERSON_CARD_RELATE_KEYS,
] as const;

// ---- display ---------------------------------------------------------------------------------------------

/**
 * A date row as the reader's calendar writes it: "10 August 1983" / "3 Av 5771" (year only when known). The
 * Hebrew month name comes from ICU in the reader's language, so a Hebrew reader sees "אב" and an English one
 * "Av" — the picker lists the same names.
 */
export function formatCardDate(row: Pick<CardDateRow, 'calendar' | 'day' | 'month' | 'hebrewMonth' | 'year'>, locale: string): string {
  if (row.calendar === 'hebrew' && row.hebrewMonth) {
    return [String(row.day), hebrewMonthName(row.hebrewMonth, locale), row.year ? String(row.year) : ''].filter(Boolean).join(' ');
  }
  const month = row.month ?? 1;
  // A malformed day (31 April) is written as stored — formatting it would roll it into the next month.
  if (row.day > GREGORIAN_MONTH_DAYS[month - 1]) {
    return [String(row.day), gregorianMonthNames(locale)[month - 1], row.year ? String(row.year) : ''].filter(Boolean).join(' ');
  }
  // A yearless 29 February still has to be formattable, so it is drawn in a leap year with the year hidden.
  const at = new Date(Date.UTC(row.year ?? 2000, month - 1, row.day));
  const options: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'long', timeZone: 'UTC', ...(row.year ? { year: 'numeric' } : {}) };
  try {
    return new Intl.DateTimeFormat(locale, options).format(at);
  } catch {
    return new Intl.DateTimeFormat('en', options).format(at);
  }
}

/** The twelve Gregorian month names in the reader's language, for the date picker. */
export function gregorianMonthNames(locale: string): string[] {
  const format = (() => {
    try {
      return new Intl.DateTimeFormat(locale, { month: 'long', timeZone: 'UTC' });
    } catch {
      return new Intl.DateTimeFormat('en', { month: 'long', timeZone: 'UTC' });
    }
  })();
  return Array.from({ length: 12 }, (_, i) => format.format(new Date(Date.UTC(2000, i, 1))));
}

export type DaysUntilKey =
  | 'people.card.dates.today'
  | 'people.card.dates.inOneDay'
  | 'people.card.dates.inTwoDays'
  | 'people.card.dates.inDays.one'
  | 'people.card.dates.inDays.other';

/**
 * "in N days". 1 and 2 have their own whole phrases (`inOneDay` / `inTwoDays`, no number to interpolate), because
 * a language with a dual cannot say them with a count: Hebrew is "בעוד יום" / "בעוד יומיים", never "בעוד 1 יום".
 * Every other count picks its plural form with the locale's own rules; only `one` and `other` are catalogued.
 */
export function daysUntilKey(days: number, locale: string): DaysUntilKey {
  if (days === 0) return 'people.card.dates.today';
  if (days === 1) return 'people.card.dates.inOneDay';
  if (days === 2) return 'people.card.dates.inTwoDays';
  let form: Intl.LDMLPluralRule = 'other';
  try {
    form = new Intl.PluralRules(locale).select(days);
  } catch {
    form = days === 1 ? 'one' : 'other';
  }
  return form === 'one' ? 'people.card.dates.inDays.one' : 'people.card.dates.inDays.other';
}

// ---- the date sheet --------------------------------------------------------------------------------------------

/** The earliest Hebrew year the date sheet accepts (1839/40 CE) — a typed "1983" is never a Hebrew year. */
export const HEBREW_YEAR_MIN = 5600;

/** The Hebrew year `today` falls in — the date sheet's year hint and, plus one, its upper bound. */
export function currentHebrewYear(today: CivilDate): number {
  return hebrewDateOf(today).year;
}

/**
 * The Hebrew months the date sheet offers. With a year typed (four digits), only the months that year has: Adar I
 * and Adar II in a leap year, plain Adar in a common one. Without one, all of them — a yearless date may be either.
 */
export function hebrewMonthsForYear(year: string): HebrewMonth[] {
  const typed = /^\d{4}$/.test(year.trim()) ? Number(year.trim()) : null;
  if (typed === null) return HEBREW_MONTHS;
  const leap = isHebrewLeapYear(typed);
  return HEBREW_MONTHS.filter((m) => (m === 'ADAR' ? !leap : m === 'ADAR_I' || m === 'ADAR_II' ? leap : true));
}

/** What the date sheet holds while it is open: the fields as typed. */
export interface DateDraft {
  hebrew: boolean;
  day: string;
  /** Gregorian month 1-12 (kept while the Hebrew calendar is on, so switching back restores it). */
  month: number;
  hebrewMonth: HebrewMonth;
  year: string;
}

/**
 * Flips the sheet's calendar. A year always belongs to its calendar, so it is never carried over as typed (Gregorian
 * 1983 is not Hebrew 1983): a complete date that exists, with a four-digit year (a Hebrew one from 5600), is
 * CONVERTED — day, month and year (10 August 1983 → 1 Elul 5743, and back); anything else keeps the day and month
 * and CLEARS the year. iOS `PersonCardEdit.DateDraft.switchingCalendar` and Android `PersonCardEdit.DateDraft.switchCalendar` agree.
 */
export function switchDateDraftCalendar(draft: DateDraft, toHebrew: boolean): DateDraft {
  if (draft.hebrew === toHebrew) return draft;
  const day = Number(draft.day);
  const year = /^\d{4}$/.test(draft.year.trim()) ? Number(draft.year.trim()) : null;
  const cleared: DateDraft = { ...draft, hebrew: toHebrew, year: '' };
  // Only a real year converts: four digits, and a Hebrew one no earlier than the sheet accepts.
  if (!Number.isInteger(day) || day < 1 || year === null || (draft.hebrew && year < HEBREW_YEAR_MIN)) return cleared;
  try {
    if (toHebrew) {
      if (day > GREGORIAN_MONTH_DAYS[draft.month - 1] || (draft.month === 2 && day === 29 && !isLeap(year))) return cleared;
      const h = hebrewDateOf({ year, month: draft.month, day });
      return { ...draft, hebrew: true, day: String(h.day), hebrewMonth: h.month, year: String(h.year) };
    }
    if (!hebrewDayExists(year, draft.hebrewMonth, day)) return cleared;
    const c = hebrewToCivil(year, draft.hebrewMonth, day);
    return { ...draft, hebrew: false, day: String(c.day), month: c.month, year: String(c.year) };
  } catch {
    return cleared;
  }
}

/**
 * The sheet's fields as the value to save, or the catalog key naming what is wrong — checked before the server
 * would, so the card can say WHICH problem without a round trip. A day the month never has, or a year outside its
 * calendar's range — Gregorian 1-9999; Hebrew 5600 (`HEBREW_YEAR_MIN`) to next year (`currentHebrewYear(today) + 1`) —
 * is `invalidDate`; a custom date with nothing saying what it marks is `labelRequired`.
 */
export function dateDraftValue(
  draft: DateDraft & { kind: CardDateKind; label: string },
  today: CivilDate,
):
  | {
      ok: true;
      value: {
        kind: string;
        calendar: 'GREGORIAN' | 'HEBREW';
        day: number;
        month?: number;
        hebrewMonth?: HebrewMonth;
        year?: number;
        label?: string;
      };
    }
  | { ok: false; errorKey: string } {
  const invalid = { ok: false as const, errorKey: 'people.card.error.invalidDate' };
  const day = Number(draft.day.trim());
  const yearText = draft.year.trim();
  const year = yearText ? Number(yearText) : null;
  const maxDay = draft.hebrew ? HEBREW_MONTH_MAX_DAYS[draft.hebrewMonth] : GREGORIAN_MONTH_DAYS[draft.month - 1];
  if (!draft.day.trim() || !Number.isInteger(day) || day < 1 || maxDay === undefined || day > maxDay) return invalid;
  if (year !== null) {
    const [min, max] = draft.hebrew ? [HEBREW_YEAR_MIN, currentHebrewYear(today) + 1] : [1, 9999];
    if (!Number.isInteger(year) || year < min || year > max) return invalid;
  }
  const label = draft.label.trim();
  if (draft.kind === 'custom' && !label) return { ok: false, errorKey: 'people.card.error.labelRequired' };
  return {
    ok: true,
    value: {
      kind: DATE_KIND_WIRE[draft.kind],
      calendar: draft.hebrew ? 'HEBREW' : 'GREGORIAN',
      day,
      ...(draft.hebrew ? { hebrewMonth: draft.hebrewMonth } : { month: draft.month }),
      ...(year !== null ? { year } : {}),
      // A label the date already carried ("wedding anniversary") is kept; only a custom date asks for one.
      ...(label ? { label: label.slice(0, 100) } : {}),
    },
  };
}

/** The wire names the card SENDS for "How you relate" — the inverse of the read maps above. */
export const FEELING_WIRE: Record<CardFeeling, string> = { close: 'CLOSE', warm: 'WARM', neutral: 'NEUTRAL', distant: 'DISTANT' };
export const CLOSENESS_WIRE: Record<CardCloseness, string> = {
  innerCircle: 'INNER_CIRCLE',
  close: 'CLOSE',
  regular: 'REGULAR',
  peripheral: 'PERIPHERAL',
};
export const REGISTER_WIRE: Record<CardRegister, string> = {
  intimate: 'INTIMATE',
  casual: 'CASUAL',
  friendlyProfessional: 'FRIENDLY_PROFESSIONAL',
  formal: 'FORMAL',
};

/**
 * The learned style as ONE line's parts, in order — register · language · greeting / sign-off · length. Each part
 * is a token the UI resolves (a catalog key, a language code, or the person's own greeting text), so this stays
 * free of i18n. The note is its own line under it.
 */
export function learnedStyleParts(style: NonNullable<CardLearned['style']>): ({ key: string } | { language: string } | { text: string })[] {
  const greeting = [style.greeting, style.signOff].filter((part): part is string => !!part).join(' / ');
  return [
    style.register ? { key: `people.card.relate.tone.${style.register}` } : null,
    style.language ? { language: style.language } : null,
    greeting ? { text: greeting } : null,
    style.length ? { key: `people.card.relate.learned.length.${style.length}` } : null,
  ].filter((part): part is { key: string } | { language: string } | { text: string } => part !== null);
}

/** An evidence line's date in the locale's MEDIUM date format ("Oct 2, 2026"); null when it does not parse. */
export function formatRelateDate(iso: string | null, locale: string): string | null {
  if (!iso) return null;
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return null;
  const options: Intl.DateTimeFormatOptions = { dateStyle: 'medium' };
  try {
    return new Intl.DateTimeFormat(locale, options).format(at);
  } catch {
    return new Intl.DateTimeFormat('en', options).format(at);
  }
}

/** A learned style's language code named in the reader's language ("he" → "Hebrew"); the code itself as a fallback. */
export function languageName(code: string, locale: string): string {
  try {
    return new Intl.DisplayNames([locale], { type: 'language' }).of(code) ?? code;
  } catch {
    return code;
  }
}

/** A date kind's wire value. */
export const DATE_KIND_WIRE: Record<CardDateKind, string> = {
  birthday: 'BIRTHDAY',
  anniversary: 'ANNIVERSARY',
  memorial: 'MEMORIAL',
  nameDay: 'NAME_DAY',
  custom: 'CUSTOM',
};
